// The sync client (webdavsync-as-an-acequia-js-capability, phase 1).
//
// A port of WebDavSync's webdav-client.js that runs anywhere `fetch` does —
// browser, service worker, Node — with the lessons that client learned kept
// intact: `?token=` query auth, the group-proxy href-namespace invariant
// (2026-07-31), server-side COPY with forwarded auth (push, and since 3.0.57
// pull: COPY to the destination with Source), and — new here —
// per-member <status> reading so an unreadable directory is reported as
// unreadable rather than as empty (2026-08-11).
//
// Server config, as the shared server-config store holds it:
//   { url, workingDirectory = '/', authType: 'token'|'basic'|'digest'|'none'|'short-code',
//     authData: { token, tokenMethod: 'header'|'query' } | { username, password } | { code } }
// 'short-code' is an Acequia short code, redeemed once at <origin>/auth/chains/id/<code>
// into a bearer leaf; unredeemed it would be a 403 on every request.

import { parseMultistatus } from './davResponse.js'
import { shouldExclude, dirMayContainIncluded } from './patterns.js'

export class HttpError extends Error {
    constructor(method, status, statusText, detail = '') {
        super(`${method} failed: ${status} ${statusText}${detail ? ` - ${detail}` : ''}`)
        this.name = 'HttpError'
        this.status = status
    }
}

function safeDecodeURIComponent(value) {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

const PROPFIND_BODY = `<?xml version="1.0" encoding="utf-8" ?>
<D:propfind xmlns:D="DAV:">
  <D:prop>
    <D:displayname/>
    <D:getcontentlength/>
    <D:getlastmodified/>
    <D:resourcetype/>
    <D:getcontenttype/>
    <D:getetag/>
  </D:prop>
</D:propfind>`

export class SyncClient {
    /**
     * @param {object|string} serverConfig - a server-config record, or a bare URL
     * @param {object} [options]
     * @param {Function} [options.fetch] - fetch implementation (default: global fetch)
     */
    constructor(serverConfig, options = {}) {
        const config = typeof serverConfig === 'string' ? { url: serverConfig } : serverConfig
        if (!config || !config.url) throw new Error('SyncClient: a server url is required')
        this.baseUrl = config.url.endsWith('/') ? config.url : config.url + '/'
        this.workingDirectory = config.workingDirectory || '/'
        this.authType = config.authType || 'none'
        this.authData = { ...(config.authData || {}) }
        // Wrapped, never stored bare: a browser's fetch refuses to run with
        // anything but the window as `this` ("Illegal invocation"); node's
        // does not care, so a test in node cannot see the difference.
        this._fetch = options.fetch || ((...args) => globalThis.fetch(...args))
        this._redeemed = null
    }

    get origin() {
        return new URL(this.baseUrl).origin
    }

    /** Resolve a short code into a bearer token, once. */
    async _ensureAuth() {
        if (this.authType !== 'short-code' || this._redeemed) return
        const code = this.authData.code
        if (!code) throw new Error('short-code auth needs authData.code')
        const response = await this._fetch(`${this.origin}/auth/chains/id/${encodeURIComponent(code)}`)
        if (!response.ok) {
            throw new HttpError('short code redemption', response.status, response.statusText)
        }
        const jwt = (await response.text()).trim()
        if (jwt.split('.').length !== 3) throw new Error('short code redemption did not return a token')
        this._redeemed = jwt
    }

    getAuthHeaders() {
        const headers = {}
        switch (this.authType) {
            case 'token':
                if (this.authData.tokenMethod !== 'query' && this.authData.token) {
                    headers['Authorization'] = `Bearer ${this.authData.token}`
                }
                break
            case 'short-code':
                if (this._redeemed) headers['Authorization'] = `Bearer ${this._redeemed}`
                break
            case 'basic':
            case 'digest':
                // Digest is answered as Basic and left to the browser's
                // challenge handling, as the app always did.
                if (this.authData.username && this.authData.password) {
                    headers['Authorization'] = `Basic ${btoa(`${this.authData.username}:${this.authData.password}`)}`
                }
                break
            default:
                break
        }
        return headers
    }

    /**
     * Can this client's auth be forwarded to a third-party server for a
     * server-side COPY? Tokens and basic can; 'none' (cookies) cannot —
     * cookies are domain-locked browser state.
     */
    hasForwardableAuth() {
        if (this.authType === 'none') return false
        if (this.authType === 'token' && !this.authData.token) return false
        if (this.authType === 'short-code' && !this._redeemed) return false
        return true
    }

    prepareUrl(path = '') {
        const url = new URL(this.baseUrl + this.combinePaths(this.workingDirectory, path))
        if (this.authType === 'token' && this.authData.token && this.authData.tokenMethod === 'query') {
            url.searchParams.set('token', this.authData.token)
        }
        return url
    }

    combinePaths(basePath, relativePath) {
        if (!basePath) basePath = '/'
        if (!basePath.startsWith('/')) basePath = '/' + basePath
        if (!basePath.endsWith('/')) basePath += '/'
        if (!relativePath) return basePath === '/' ? '' : basePath.slice(1)
        if (relativePath.startsWith('/')) relativePath = relativePath.slice(1)
        return basePath === '/' ? relativePath : basePath.slice(1) + relativePath
    }

    async _request(method, path, init = {}) {
        await this._ensureAuth()
        const url = this.prepareUrl(path)
        return this._fetch(url, {
            ...init,
            method,
            headers: { ...this.getAuthHeaders(), ...(init.headers || {}) },
        })
    }

    async propfind(path = '', depth = 1) {
        const response = await this._request('PROPFIND', path, {
            headers: { Depth: String(depth), 'Content-Type': 'application/xml' },
            body: PROPFIND_BODY,
        })
        if (!response.ok) throw new HttpError('PROPFIND', response.status, response.statusText)
        return this.parsePropfindResponse(await response.text())
    }

    async get(path) {
        const response = await this._request('GET', path)
        if (!response.ok) throw new HttpError('GET', response.status, response.statusText)
        return response
    }

    async put(path, content, headers = {}) {
        const response = await this._request('PUT', path, { body: content, headers })
        if (!response.ok) throw new HttpError('PUT', response.status, response.statusText)
        return response
    }

    async mkcol(path) {
        const response = await this._request('MKCOL', path)
        // 405: the collection already exists
        if (!response.ok && response.status !== 405) {
            throw new HttpError('MKCOL', response.status, response.statusText)
        }
        return response
    }

    async delete(path) {
        const response = await this._request('DELETE', path)
        if (!response.ok) throw new HttpError('DELETE', response.status, response.statusText)
        return response
    }

    async proppatch(path, properties) {
        let props = ''
        for (const [name, value] of Object.entries(properties)) {
            const text = name === 'getlastmodified' && value instanceof Date ? value.toUTCString() : value
            props += `<D:${name}>${text}</D:${name}>`
        }
        const response = await this._request('PROPPATCH', path, {
            headers: { 'Content-Type': 'application/xml' },
            body: `<?xml version="1.0" encoding="utf-8" ?>
<D:propertyupdate xmlns:D="DAV:"><D:set><D:prop>${props}</D:prop></D:set></D:propertyupdate>`,
        })
        if (!response.ok) throw new HttpError('PROPPATCH', response.status, response.statusText)
        const body = await response.text()
        if (body.includes('403 Forbidden') && body.includes('protected property')) {
            throw new Error('Property is protected and cannot be modified')
        }
        return response
    }

    /**
     * Client-mediated copy: GET here, PUT there, through this context. The
     * body is buffered (streaming uploads need HTTP/2 end to end).
     */
    async copyTo(sourcePath, destinationClient, destinationPath, onProgress = null) {
        const source = await this.get(sourcePath)
        const declared = source.headers.get('Content-Length')
        const blob = await source.blob()
        const transferredBytes = blob.size
        onProgress?.(declared ? parseInt(declared) : transferredBytes, 0, false)
        const response = await destinationClient.put(destinationPath, blob)
        onProgress?.(declared ? parseInt(declared) : transferredBytes, transferredBytes, true)
        return {
            status: response.status,
            totalSize: declared ? parseInt(declared) : transferredBytes,
            transferredBytes,
        }
    }

    /**
     * Server-side COPY (third-party copy): the source server PUTs to the
     * destination itself, the destination's auth forwarded as
     * TransferHeaderAuthorization. Returns the raw response; the engine
     * reads the status to decide whether to fall back.
     */
    async serverCopy(sourcePath, destinationClient, destinationPath, options = {}) {
        await destinationClient._ensureAuth()
        const headers = {
            Destination: destinationClient.prepareUrl(destinationPath).toString(),
            Overwrite: options.overwrite !== false ? 'T' : 'F',
        }
        const destAuth = destinationClient.getAuthHeaders()
        if (destAuth['Authorization']) headers['TransferHeaderAuthorization'] = destAuth['Authorization']
        return this._request('COPY', sourcePath, { headers })
    }

    /**
     * Server-side pull (HTTP-TPC pull mode): this client's server is the
     * destination and fetches the file from the source itself — a COPY to the
     * destination path with `Source`, the source's auth forwarded as
     * TransferHeaderAuthorization. The answer is 201/204 (BrowserDAV), or 202
     * with performance markers ending in a success or failure line (the node
     * server); the engine reads it. Returns the raw response.
     */
    async serverPull(sourceClient, sourcePath, destinationPath, options = {}) {
        await sourceClient._ensureAuth()
        const headers = {
            Source: sourceClient.prepareUrl(sourcePath).toString(),
            Overwrite: options.overwrite !== false ? 'T' : 'F',
        }
        const srcAuth = sourceClient.getAuthHeaders()
        if (srcAuth['Authorization']) headers['TransferHeaderAuthorization'] = srcAuth['Authorization']
        return this._request('COPY', destinationPath, { headers })
    }

    /**
     * Multistatus → items relative to the working directory:
     *   { path, name, size, lastModified, etag, isDirectory, href, status }
     * Refused members (401/403) come back with their status and no props.
     */
    parsePropfindResponse(xmlText) {
        const baseUrlObj = new URL(this.baseUrl)
        const basePathname = safeDecodeURIComponent(baseUrlObj.pathname)
        const items = []
        for (const record of parseMultistatus(xmlText)) {
            const decodedHref = safeDecodeURIComponent(record.href)
            let hrefPath = record.href
            try {
                const hrefUrl = new URL(record.href)
                // Same host: keep only the pathname (the scheme may differ).
                if (hrefUrl.hostname === baseUrlObj.hostname) hrefPath = hrefUrl.pathname
            } catch {
                // a bare path
            }
            // Parse the RAW href first, decode the extracted pathname once.
            hrefPath = safeDecodeURIComponent(hrefPath)
            let relativePath
            if (hrefPath.startsWith('/')) {
                // Namespace invariant: the first response is the requested
                // resource, so its href must live under the base path. A
                // server or proxy answering in another namespace would make
                // every computed path garbage — refuse loudly.
                if (items.length === 0 && !hrefPath.startsWith(basePathname)) {
                    throw new Error(
                        `Server returned hrefs outside the request namespace: ` +
                            `expected paths under "${basePathname}" but got "${hrefPath}". ` +
                            `The server or a proxy in between is answering in a different URL namespace; ` +
                            `refusing to compare file lists built from mismatched paths.`
                    )
                }
                relativePath = hrefPath.startsWith(basePathname) ? hrefPath.substring(basePathname.length) : hrefPath
            } else {
                relativePath = hrefPath
            }
            if (this.workingDirectory !== '/') {
                const wd = this.workingDirectory.replace(/^\/+|\/+$/g, '')
                const stripped = relativePath.replace(/^\/+/, '')
                if (stripped === wd) relativePath = ''
                else if (stripped.startsWith(wd + '/')) relativePath = stripped.substring(wd.length + 1)
            }
            relativePath = relativePath.replace(/^\/+/, '')
            const p = record.props
            items.push({
                path: relativePath,
                name: p.displayname || relativePath.split('/').pop() || 'root',
                size: p.getcontentlength ? parseInt(p.getcontentlength) : 0,
                lastModified: p.getlastmodified ? new Date(p.getlastmodified) : null,
                etag: p.getetag ? p.getetag.replace(/"/g, '') : null,
                contentType: p.getcontenttype || null,
                isDirectory: p.isCollection,
                href: decodedHref,
                status: record.status,
            })
        }
        return items
    }

    /**
     * Walk a tree breadth-first.
     * @returns {{ items: object[], unreadable: Array<{path, status, error}> }}
     *   `unreadable` names every directory whose listing failed, and every
     *   member a listing marked with a non-2xx status (refused 401/403, or
     *   any other) — the regions the engine must not reason about as absent.
     */
    async listRecursive(path = '', { onProgress = null, exclude = null, include = null, signal = null } = {}) {
        const items = []
        const unreadable = []
        const queue = [path]
        const visited = new Set()
        let processedDirs = 0
        let skippedDirs = 0
        while (queue.length > 0) {
            signal?.throwIfAborted?.()
            const currentPath = queue.shift()
            const normalizedCurrent = currentPath.replace(/\/+$/, '')
            if (visited.has(normalizedCurrent)) continue
            visited.add(normalizedCurrent)
            processedDirs++
            if (onProgress && processedDirs % 5 === 0) {
                const dirName = currentPath.length > 40 ? '...' + currentPath.slice(-40) : currentPath
                const skipMsg = skippedDirs > 0 ? `, ${skippedDirs} excluded dirs skipped` : ''
                onProgress(`Scanning directory: ${dirName} (${items.length} items found${skipMsg})`)
            }
            const propfindPath = currentPath === '' ? '' : currentPath.endsWith('/') ? currentPath : currentPath + '/'
            let listing
            try {
                listing = await this.propfind(propfindPath, 1)
            } catch (error) {
                unreadable.push({ path: normalizedCurrent, status: error.status ?? null, error: error.message })
                continue
            }
            for (const item of listing) {
                const itemPath = item.path.replace(/\/+$/, '')
                if (itemPath === normalizedCurrent) continue
                // Any non-2xx member is a region we did not see: refused
                // (401/403), or a 404/500 a server marks a member it cannot
                // stat. Never an item — it carries no properties, and an item
                // without properties reads as a zero-byte file (2026-10-06).
                if (item.status && (item.status < 200 || item.status >= 300)) {
                    unreadable.push({ path: itemPath, status: item.status, error: null })
                    continue
                }
                if (item.isDirectory && exclude && shouldExclude(item.path, exclude)) {
                    skippedDirs++
                    continue
                }
                if (item.isDirectory && include && !dirMayContainIncluded(itemPath, include)) {
                    skippedDirs++
                    continue
                }
                items.push(item)
                if (item.isDirectory) queue.push(item.path)
            }
        }
        onProgress?.(`Directory scan complete: ${items.length} items found`)
        return { items, unreadable }
    }

    async directoryExists(path) {
        try {
            const listing = await this.propfind(path, 0)
            return listing.length > 0 && listing[0].isDirectory
        } catch (error) {
            if (error.status === 404) return false
            // Unsure — assume it exists rather than fire a redundant MKCOL.
            return true
        }
    }

    async createDirectoryIfNotExists(path) {
        if (await this.directoryExists(path)) return { created: false, existed: true }
        await this.mkcol(path)
        return { created: true, existed: false }
    }

    async testPropPatchSupport() {
        try {
            await this.proppatch('', { 'test-proppatch': 'test-value' })
            await this.proppatch('', { getlastmodified: new Date() })
            return { supported: true, error: null }
        } catch (error) {
            if (error.status === 405 || error.status === 501) {
                return { supported: false, error: 'PROPPATCH method not supported by server' }
            }
            if (error.message.includes('protected property')) {
                return { supported: false, error: 'getlastmodified property is protected and cannot be modified' }
            }
            return { supported: false, error: error.message }
        }
    }

    /**
     * Reachable, and how much of the top level this credential reads: a
     * Depth: 1 listing of the root, naming every member marked non-2xx.
     * `partial` is about the top level only — a deeper refusal shows up in
     * the scan's `unreadable`. Before 2026-10-06 this was a Depth: 0 probe,
     * true for any credential that could see the root at all.
     * @returns {{ success: boolean, partial?: boolean, refused?: Array<{path, status}>, members?: number, error?: string, status?: number|null }}
     */
    async testConnection() {
        try {
            const listing = await this.propfind('', 1)
            const members = listing.filter((i) => i.path.replace(/\/+$/, '') !== '')
            const refused = members
                .filter((i) => i.status && (i.status < 200 || i.status >= 300))
                .map((i) => ({ path: i.path.replace(/\/+$/, ''), status: i.status }))
            return { success: true, partial: refused.length > 0, refused, members: members.length }
        } catch (error) {
            return { success: false, error: error.message, status: error.status ?? null }
        }
    }
}
